import type { ReactNode } from "react";

interface SectionHeadingProps {
  eyebrow: string;
  title: string;
  desc?: string;
  align?: "left" | "center";
}

export function SectionHeading({ eyebrow, title, desc, align = "left" }: SectionHeadingProps) {
  const alignCls = align === "center" ? "items-center text-center mx-auto" : "items-start";
  return (
    <div className={`flex flex-col ${alignCls} max-w-2xl`}>
      <span className="inline-flex items-center gap-2 text-sm font-label font-bold tracking-[0.18em] text-primary-600">
        <span className="h-px w-6 bg-primary-500" />
        {eyebrow}
        {align === "center" && <span className="h-px w-6 bg-primary-500" />}
      </span>
      <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
        {title}
      </h2>
      {desc && <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{desc}</p>}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  let cls = "bg-secondary-100 text-secondary-900";
  let dot = "bg-secondary-500";
  if (status === "已核实") {
    cls = "bg-accent-100 text-accent-900";
    dot = "bg-accent-500";
  } else if (status === "核查中") {
    cls = "bg-primary-50 text-primary-700 ring-1 ring-primary-200";
    dot = "bg-primary-500";
  } else if (status === "有回应") {
    cls = "bg-background-100 text-foreground-700 ring-1 ring-background-300";
    dot = "bg-foreground-400";
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
}

export function CategoryTag({ label, active, onClick }: { label: string; active?: boolean; onClick?: () => void }) {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer ${
          active
            ? "bg-primary-500 text-background-50"
            : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
        }`}
      >
        {label}
      </button>
    );
  }
  return (
    <span
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
        active ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900"
      }`}
    >
      {label}
    </span>
  );
}

export function SectionShell({ id, className, children }: { id?: string; className?: string; children: ReactNode }) {
  return (
    <section id={id} className={`scroll-mt-20 ${className ?? ""}`}>
      <div className="mx-auto w-full max-w-[1180px] px-4 md:px-6">{children}</div>
    </section>
  );
}